import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CelebrityCard from './components/CelebrityCard';
import { celebrityPosts, categories } from '../../mocks/celebritySaju';
import { celebrityDreamMap } from '../../mocks/celebrityDreams';
import SEOSchema from '../../components/feature/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const celebritySchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Blog',
      '@id': `${siteUrl}/celebrity-saju`,
      url: `${siteUrl}/celebrity-saju`,
      name: '유명인 사주·점성·자미두수 종합풀이',
      description: '일론 머스크, 트럼프, 손흥민 등 세계적 유명인의 사주팔자, 서양 점성술, 자미두수를 통합 분석하여 그들의 운명을 풀어냅니다.',
      inLanguage: 'ko-KR',
      publisher: {
        '@type': 'Organization',
        name: 'DESTINY GPT',
        url: siteUrl,
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '홈', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '유명인 사주 분석', item: `${siteUrl}/celebrity-saju` },
      ],
    },
  ],
};

const DREAM_FILTERS = [
  { id: 'all', label: '전체', icon: '🌟', desc: '전체 인물' },
  { id: '목(木)', label: '목(木)꿈', icon: '🌿', desc: '성장·창조꿈' },
  { id: '화(火)', label: '화(火)꿈', icon: '🔥', desc: '불·태양꿈' },
  { id: '토(土)', label: '토(土)꿈', icon: '🌍', desc: '대지·안정꿈' },
  { id: '금(金)', label: '금(金)꿈', icon: '⚔️', desc: '금속·용꿈' },
  { id: '수(水)', label: '수(水)꿈', icon: '💧', desc: '물·변화꿈' },
];

// Dream type to display labels
const DREAM_TYPE_INFO: Record<string, { popular: string; example: string }> = {
  '목(木)': { popular: '용꿈·나무꿈', example: '성장과 창조의 에너지' },
  '화(火)': { popular: '불꿈·태양꿈', example: '열정과 권위의 상징' },
  '토(土)': { popular: '돼지꿈·황금밭꿈', example: '안정과 재물의 기운' },
  '금(金)': { popular: '검꿈·보석꿈', example: '결단과 명예의 상징' },
  '수(水)': { popular: '뱀꿈·물꿈', example: '지혜와 변화의 흐름' },
};

export default function CelebrityBlogPage() {
  const [activeCategory, setActiveCategory] = useState('전체');
  const [activeDreamFilter, setActiveDreamFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = celebrityPosts.filter((post) => {
    const matchesCategory = activeCategory === '전체' || post.category === activeCategory;
    const matchesSearch =
      searchQuery === '' ||
      post.name.includes(searchQuery) ||
      post.nameEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some((t) => t.includes(searchQuery));
    const matchesDream =
      activeDreamFilter === 'all' ||
      (celebrityDreamMap[post.slug]?.elementDream === activeDreamFilter);
    return matchesCategory && matchesSearch && matchesDream;
  });

  const featured = filtered[0];
  const rest = filtered.slice(1);

  const dreamInfo = activeDreamFilter !== 'all' ? DREAM_TYPE_INFO[activeDreamFilter] : null;

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      <SEOSchema schema={celebritySchema} id="celebrity-schema" />
      <Navbar />

      {/* Hero Header */}
      <section className="pt-28 pb-12 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          {/* Decorative badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-gold/10 border border-primary-gold/30 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-gold animate-pulse"></span>
            <span className="text-primary-gold text-xs font-semibold tracking-widest uppercase">Celebrity Destiny</span>
          </div>

          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 leading-tight"
            style={{ fontFamily: '"Noto Serif KR", serif' }}>
            유명인{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-gold via-amber-300 to-primary-gold">
              사주·점성·자미두수
            </span>{' '}
            종합풀이
          </h1>
          <p className="text-purple-300 text-base leading-relaxed max-w-xl mx-auto">
            일론 머스크, 트럼프, 손흥민 등 세계적 유명인의 사주팔자, 서양 점성술, 자미두수를
            통합 분석하여 그들의 운명을 풀어냅니다.
          </p>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mt-8">
            {[
              { label: '분석 인물', value: `${celebrityPosts.length}명` },
              { label: '분석 체계', value: '3종 통합' },
              { label: '월 업데이트', value: '4회' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-2xl font-bold text-primary-gold">{stat.value}</p>
                <p className="text-xs text-purple-400 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 pb-20">
        {/* Search + Category Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-5">
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-purple-400 text-sm"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="이름 또는 키워드로 검색..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-purple-500 text-sm focus:outline-none focus:border-primary-gold/50 transition-colors"
            />
          </div>

          {/* Category filters */}
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-primary-gold text-black'
                    : 'bg-white/5 text-purple-300 border border-white/10 hover:border-primary-gold/40 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Dream Type Filter */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-moon-foggy-fill text-primary-gold text-sm"></i>
            </div>
            <span className="text-purple-300/80 text-xs font-semibold">꿈 유형으로 필터링</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {DREAM_FILTERS.map((df) => (
              <button
                key={df.id}
                onClick={() => setActiveDreamFilter(df.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                  activeDreamFilter === df.id
                    ? 'bg-indigo-600/40 border-indigo-400/60 text-white'
                    : 'bg-white/3 border-white/10 text-purple-300 hover:border-indigo-400/30 hover:text-white hover:bg-indigo-500/10'
                }`}
              >
                <span>{df.icon}</span>
                <span>{df.label}</span>
                {df.id !== 'all' && (
                  <span className="text-purple-400/60 hidden sm:inline">· {df.desc}</span>
                )}
              </button>
            ))}
          </div>

          {/* Dream filter info banner */}
          {dreamInfo && activeDreamFilter !== 'all' && (
            <div className="mt-3 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-indigo-900/30 border border-indigo-500/25">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-indigo-300 text-sm"></i>
              </div>
              <p className="text-indigo-200/80 text-xs">
                <strong>{activeDreamFilter} 꿈 유형</strong> — {dreamInfo.popular}이 어울리는 사주 · {dreamInfo.example} · {filtered.length}명 해당
              </p>
            </div>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-eye-line text-5xl text-purple-500/50"></i>
            </div>
            <p className="text-purple-400">검색 결과가 없습니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Featured + rest list */}
            <div className="lg:col-span-2 space-y-6">
              {featured && <CelebrityCard post={featured} featured />}

              <div className="space-y-3">
                {rest.map((post) => (
                  <CelebrityCard key={post.id} post={post} />
                ))}
              </div>
            </div>

            {/* Right: Sidebar */}
            <aside className="space-y-6">
              {/* Analysis system explanation */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2"
                  style={{ fontFamily: '"Noto Serif KR", serif' }}>
                  <span className="text-primary-gold">三</span> 통합 분석 체계
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-amber-500/20 border border-amber-500/30">
                      <span className="text-amber-400 text-xs font-bold">命</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">사주팔자 (四柱八字)</p>
                      <p className="text-purple-400 text-xs mt-0.5">생년·월·일·시의 4기둥으로 운명의 구조를 파악</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-purple-500/20 border border-purple-500/30">
                      <span className="text-purple-400 text-xs font-bold">★</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">서양 점성술</p>
                      <p className="text-purple-400 text-xs mt-0.5">태양궁·달자리·상승궁으로 성격과 운명 분석</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-teal-500/20 border border-teal-500/30">
                      <span className="text-teal-400 text-xs font-bold">紫</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">자미두수 (紫微斗數)</p>
                      <p className="text-purple-400 text-xs mt-0.5">명반의 주성으로 타고난 삶의 패턴을 해석</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dream filter guide */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-indigo-900/40 to-purple-900/30 border border-indigo-500/25">
                <h3 className="text-white font-bold mb-3 text-sm flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-moon-foggy-fill text-indigo-300 text-sm"></i>
                  </div>
                  꿈 유형 가이드
                </h3>
                <div className="space-y-2">
                  {DREAM_FILTERS.filter((f) => f.id !== 'all').map((df) => {
                    const info = DREAM_TYPE_INFO[df.id];
                    const count = celebrityPosts.filter(
                      (p) => celebrityDreamMap[p.slug]?.elementDream === df.id
                    ).length;
                    return (
                      <button
                        key={df.id}
                        onClick={() => setActiveDreamFilter(df.id)}
                        className={`w-full flex items-center gap-2.5 p-2 rounded-lg text-left transition-all cursor-pointer ${
                          activeDreamFilter === df.id
                            ? 'bg-indigo-600/20 border border-indigo-400/30'
                            : 'hover:bg-white/5 border border-transparent'
                        }`}
                      >
                        <span className="text-base flex-shrink-0">{df.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold">{df.label}</p>
                          <p className="text-purple-400 text-[10px]">{info?.popular}</p>
                        </div>
                        <span className="text-primary-gold text-xs font-bold flex-shrink-0">{count}명</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* All posts quick list */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
                <h3 className="text-white font-bold mb-4"
                  style={{ fontFamily: '"Noto Serif KR", serif' }}>
                  전체 인물 목록
                </h3>
                <div className="space-y-2">
                  {celebrityPosts.map((post) => {
                    const dreamElem = celebrityDreamMap[post.slug]?.elementDream;
                    return (
                      <Link
                        key={post.id}
                        to={`/celebrity-saju/${post.slug}`}
                        className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 transition-colors group cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={post.thumbnail} alt={post.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold group-hover:text-primary-gold transition-colors truncate">{post.name}</p>
                          <p className="text-purple-500 text-xs">{post.dayMaster} · {dreamElem ? dreamElem + '꿈' : ''}</p>
                        </div>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-purple-500 group-hover:text-primary-gold transition-colors text-sm"></i>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* CTA */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-primary-gold/10 to-amber-900/10 border border-primary-gold/20">
                <h3 className="text-primary-gold font-bold mb-2 text-sm">내 사주도 분석해보세요</h3>
                <p className="text-purple-300 text-xs leading-relaxed mb-4">
                  유명인처럼 사주팔자 + 점성술 + 자미두수 3종 통합 분석을 AI로 받아보세요.
                </p>
                <Link
                  to="/"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-primary-gold text-black text-sm font-bold hover:bg-amber-400 transition-colors whitespace-nowrap"
                >
                  <span>내 운명 분석 시작</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </aside>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
